/**
 * Loss History Detail (#33939) — the ONE writer. Every source (manual add, IMT import, A+ pull)
 * builds an entry through `makeLossHistoryEntry`, so date normalization, null defaulting, and the
 * full field set live in one place instead of four hand-written copies.
 */
import { v4 as uuidv4 } from 'uuid';
import { APLUS_LOSS_SOURCE, IMT_CLAIM_SOURCE, MANUAL_ENTRY_SOURCE } from './constants';
import { normalizeLossDate } from './normalize';
import type {
  AplusLocationClaims,
  AplusLossHistoryContext,
  AplusUwClaim,
  ImtClaim,
  ImtLossHistoryContext,
  LossHistoryEntry,
  ManualEntryValues
} from './types';

/** '' / undefined → null; otherwise the value unchanged. */
const orNull = <T>(value: T | '' | undefined): T | null => (value === '' || value === undefined ? null : value);

/** '' / undefined / null → null; otherwise Number(value). */
const toNumberOrNull = (value: number | string | null | undefined): number | null =>
  value === '' || value === undefined || value === null ? null : Number(value);

/**
 * Build a canonical entry from a partial. Fills every field to a null default, normalizes
 * `lossDate` to ISO, and defaults `ignore` to false — so callers only supply what they have and
 * every entry has the same complete shape.
 */
export function makeLossHistoryEntry(partial: Partial<LossHistoryEntry> & { id: string }): LossHistoryEntry {
  return {
    id: partial.id,
    source: partial.source ?? null,
    ignore: partial.ignore ?? false,
    lossDate: normalizeLossDate(partial.lossDate),
    paidAmount: partial.paidAmount ?? null,
    lossAmount: partial.lossAmount ?? null,
    causeOfLoss: partial.causeOfLoss ?? null,
    lossDescription: partial.lossDescription ?? null,
    claimNumber: partial.claimNumber ?? null,
    claimStatus: partial.claimStatus ?? null,
    policyNumber: partial.policyNumber ?? null,
    comments: partial.comments ?? null,
    carrierName: partial.carrierName ?? null,
    catastropheNumber: partial.catastropheNumber ?? null,
    policyType: partial.policyType ?? null,
    property: partial.property ?? null,
    locationId: partial.locationId ?? null,
    claimGroup: partial.claimGroup ?? null,
    matchReason: partial.matchReason ?? null,
    addedBy: partial.addedBy ?? null
  };
}

// Deterministic, position-independent id. A djb2 hash of the identifying fields (base36) keeps a
// re-pull idempotent AND stops a dropped/reordered claim from shifting its neighbours' ids — the
// index-in-id bug (#33957) where a UW's Ignore could land on a different claim. An ordinal is added
// ONLY to disambiguate entries that are identical on every identifying field (e.g. two outbuildings
// lost in one storm, same date + same payout), so those still get distinct, stable ids.
function hash(parts: Array<string | number | null | undefined>): string {
  const key = parts.map((p) => (p === null || p === undefined ? 'NA' : String(p))).join('|');
  let h = 5381;
  for (let i = 0; i < key.length; i++) {
    h = (h * 33) ^ key.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
}

/**
 * Assign an ordinal within each group of entries that share the same identity hash, so identical
 * entries in one build get distinct ids (`<prefix>-<hash>` / `<prefix>-<hash>-1` / `-2` …) while a
 * unique entry keeps a bare, stable id.
 */
function withGroupOrdinal(prefix: string, identityHashes: string[]): string[] {
  const seen = new Map<string, number>();
  return identityHashes.map((h) => {
    const n = seen.get(h) ?? 0;
    seen.set(h, n + 1);
    return n === 0 ? `${prefix}-${h}` : `${prefix}-${h}-${n}`;
  });
}

/** Build a persisted manual entry from the add-form values (id is a fresh uuid). */
export function buildManualEntry(values: ManualEntryValues): LossHistoryEntry {
  return makeLossHistoryEntry({
    id: uuidv4(),
    source: MANUAL_ENTRY_SOURCE,
    ignore: false,
    causeOfLoss: orNull(values.causeOfLoss),
    lossDate: orNull(values.lossDate),
    catastropheNumber: orNull(values.catastropheNumber),
    claimNumber: orNull(values.claimNumber),
    policyType: orNull(values.policyType),
    carrierName: orNull(values.carrierName),
    paidAmount: toNumberOrNull(values.paidAmount),
    comments: orNull(values.comments),
    lossDescription: orNull(values.lossDescription),
    lossAmount: toNumberOrNull(values.lossAmount),
    claimStatus: orNull(values.claimStatus),
    property: orNull(values.property),
    policyNumber: orNull(values.policyNumber)
    // addedBy intentionally omitted — the backend stamps it from the acting user's identity on save.
  });
}

/** Build persisted entries from IMT `_claims.json` claims, with policy-level defaults. */
export function buildImtLossHistoryEntries(
  claims: ImtClaim[] | undefined | null,
  context: ImtLossHistoryContext = {}
): LossHistoryEntry[] {
  const list = claims || [];
  // Prefer the source claim number for the id; fall back to a content hash + group ordinal.
  const fallbackIds = withGroupOrdinal(
    'imt-claim',
    list.map((c) => hash([normalizeLossDate(c.lossDate), c.amount]))
  );
  return list.map((claim, index) =>
    makeLossHistoryEntry({
      id: claim.claimNumber ? `imt-claim-${claim.claimNumber}` : fallbackIds[index],
      source: IMT_CLAIM_SOURCE,
      ignore: false,
      lossDate: claim.lossDate,
      // Paid Amount = ClaimPaid (indemnity); Loss Amount = TotalLossPaid (incl. adjusting expense).
      paidAmount: claim.amount ?? null,
      lossAmount: claim.lossAmount ?? null,
      causeOfLoss: claim.causeOfLoss ?? null,
      lossDescription: claim.lossDescription ?? null,
      claimNumber: claim.claimNumber ?? null,
      claimStatus: claim.claimStatus ?? null,
      policyNumber: claim.policyNumber ?? null,
      comments: claim.comments ?? null,
      carrierName: context.carrierName ?? null,
      policyType: context.policyType ?? null,
      property: context.property ?? null
    })
  );
}

/** Build persisted entries from the A+/Verisk property pull, with policy-level defaults. */
export function buildAplusLossHistoryEntries(
  claimsByLocation: AplusLocationClaims[] | undefined | null,
  context: AplusLossHistoryContext = {}
): LossHistoryEntry[] {
  const entries: LossHistoryEntry[] = [];
  for (const location of claimsByLocation || []) {
    const claims: AplusUwClaim[] = location.claims || [];
    // Content-hash id scoped to the location, with a group ordinal for identical claims.
    const ids = withGroupOrdinal(
      `aplus-${location.locationId}`,
      claims.map((c) => hash([normalizeLossDate(c.lossDate), c.amount, c.lossTypeCode, c.claimGroup]))
    );
    claims.forEach((claim, index) => {
      entries.push(
        makeLossHistoryEntry({
          id: ids[index],
          source: APLUS_LOSS_SOURCE,
          ignore: false,
          lossDate: claim.lossDate,
          paidAmount: claim.amount ?? null,
          // The reduced A+ shape carries a single paid figure; no separate incurred/loss amount.
          lossAmount: null,
          causeOfLoss: claim.lossTypeCode ?? null,
          claimGroup: claim.claimGroup ?? null,
          matchReason: claim.matchReason ?? null,
          carrierName: context.carrierName ?? null,
          policyType: context.policyType ?? null,
          locationId: location.locationId,
          property: location.property ?? context.property ?? null
        })
      );
    });
  }
  return entries;
}
